import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './CurrencySelect.module.scss';
import AppSelect from 'shared/components/AppSelect';
import classnames from 'classnames';
import { getKeyPressCallback, getCurrenciesOptions, filterCurrencies, scrollToOption } from './Currency.utils';
import { CurrencySelectionStepProps, CurrencyOption, SelectKeyboardListenersNames, Currency } from './types';
import { palette } from 'shared/constants/colorsConstants';

export const CurrencySelect: React.FC<CurrencySelectionStepProps> = ({
  selectedCurrency,
  currenciesArray,
  setSelectedCurrency,
  indicatorComponent,
  classes,
  disabled,
  sessionCurrency,
  processingCurrency,
  providerCurrency,
  error = '',
  outline = 'default',
  backgroundColor = palette.white[500],
  controlBackgroundColor = palette.gray[200],
}) => {
  const selected = currenciesArray.find((currency) => currency.code === selectedCurrency);
  const [keyboardSelectedCurrency, setKeyboardSelectedCurrency] = useState<Currency | null>(selected!);
  const [menuClosed, setMenuClosed] = useState<boolean | null>(false);
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [filteredOptions, setFilteredOptions] = useState<Currency[]>([]);
  const [keyPressDirection, setKeyPressDirection] = useState<SelectKeyboardListenersNames | null>(null);
  const [options, setOptions] = useState<CurrencyOption[]>([]);

  useEffect(() => {
    if (selectedRef.current) {
      scrollToOption(selectedRef, keyPressDirection);
    }
  }, [keyboardSelectedCurrency]);

  useEffect(() => {
    const filtered = filterCurrencies(currenciesArray, searchInputValue);

    setFilteredOptions(filtered);

    if (filtered.length > 0 && searchInputValue) {
      setKeyboardSelectedCurrency(filtered[0]);
    }
  }, [currenciesArray, searchInputValue]);

  useEffect(() => {
    const localOptions = getCurrenciesOptions({
      keyboardSelectedCurrency,
      filteredOptions,
      currenciesArray,
      selectedRef,
      sessionCurrency,
      processingCurrency,
      providerCurrency,
    });

    setOptions(localOptions);
  }, [
    currenciesArray,
    keyboardSelectedCurrency,
    filteredOptions,
    sessionCurrency,
    processingCurrency,
    providerCurrency,
  ]);
  const handleKeyPress = useMemo(
    () =>
      getKeyPressCallback({
        filteredOptions,
        currenciesArray,
        keyboardSelectedCurrency,
        setSearchInputValue,
        setSelectedCurrency,
        setMenuClosed,
        setKeyboardSelectedCurrency,
        setKeyPressDirection,
      }),
    [filteredOptions, currenciesArray, keyboardSelectedCurrency, setSelectedCurrency, setMenuClosed],
  );
  return (
    <div className={classnames(classes, disabled ? styles.disabled : '')}>
      <AppSelect
        onKeyDown={handleKeyPress}
        searchValue={searchInputValue}
        disabled={disabled}
        isClearable={false}
        menuIsOpen={(menuClosed as boolean) ? false : (null as unknown as undefined)}
        isSearchable={true}
        automation-id="processing-currency-select"
        className={styles.select}
        value={selectedCurrency}
        options={options}
        onChange={() => {}}
        onClick={(selectedOption) => {
          setSelectedCurrency(selectedOption);
        }}
        outline={error ? `2px solid ${palette.red[300]}` : outline}
        controlFontSize={14}
        optionsStyle={{
          padding: '0px',
          backgroundColor,
          controlBackgroundColor,
        }}
        optionLabelClass={styles.optionLabel}
        optionInnerSubLabel={styles.optionInnerSubLabel}
        indicatorDisplay={'show'}
        indicatorComponent={indicatorComponent}
        keyboardSelectedItem={keyboardSelectedCurrency?.code}
        autoFocus={false}
      />
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};
